/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { FormControl, FormGroup, NgForm, Validators } from '@angular/forms';
import { DatePickerModule } from 'primeng/datepicker';
import { Subscription } from 'rxjs';
import { FileDto } from 'src/app/models/FileDto';
import { GenericColumnConfiguration } from 'src/app/models/GenericColumnConfiguration';
import { GenericTableConfiguration } from 'src/app/models/GenericTableConfiguration';
import { FormChangeDetectionService } from 'src/app/services/form-change-detection.service';
import { SHARED_IMPORTS } from 'src/app/utils/other/standalone-import';
import { AutocompleteComponent } from './form-fields/autocomplete/autocomplete.component';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { FileUploadComponent } from '../../../misc/file-upload/file-upload.component';
import { DynamicFormCompComponent } from './form-fields/dynamic-form-comp/dynamic-form-comp.component';
import { FilesComponent } from 'src/app/components/misc/file-upload/files/files.component';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { InputTextModule } from 'primeng/inputtext';

@Component({
  standalone: true,
  imports: [
    SHARED_IMPORTS,
    DatePickerModule,
    AutocompleteComponent,
    InputNumberModule,
    SelectModule,
    FileUploadComponent,
    DynamicFormCompComponent,
    FilesComponent,
    ToggleSwitchModule,
    InputTextModule
  ],
  selector: 'app-form',
  templateUrl: './form.component.html',
  styleUrls: ['./form.component.scss']
})
export class FormComponent implements OnInit, OnDestroy {
  /**
   * Reference to the form element in the template.
   * Used to access the form's properties and methods.
   */
  @ViewChild('form') formElement!: NgForm;

  /**
   * The configuration for the generic table.
   */
  @Input() config!: GenericTableConfiguration;

  /**
   * The object being edited.
   */
  @Input() editObject: any;

  /**
   * Indicates whether the form is displayed in a separate page or in sidebar.
   */
  @Input() isInSeparatePage!: boolean;

  /**
   * Event emitted when the form changes.
   */
  @Output() formChanged = new EventEmitter<boolean>();

  /**
   * Event emitted when a file is removed.
   */
  @Output() fileRemovedEmitter = new EventEmitter<File | FileDto>();

  /**
   * The form group for the form controls.
   */
  form: FormGroup = new FormGroup({});

  /**
   * Subscription for form value changes.
   */
  formSubscription: Subscription = new Subscription();

  /**
   * The highest slot index from the formSeparatePageIndex of all columns.
   */
  highestSlotIndex = 0;

  /**
   * An array of numbers from 1 to the highest slot index.
   */
  previousNumbersArray: number[] = [];

  private formChangeDetectionService = inject(FormChangeDetectionService);

  ngOnInit() {
    // Calculate the highest slot index from the formSeparatePageIndex of all columns
    this.highestSlotIndex = Math.max(
      ...this.config.columns.map((col) => col.formSeparatePageIndex || 0)
    );

    // Create an array of numbers from 1 to the highest slot index
    this.previousNumbersArray = Array.from(
      { length: this.highestSlotIndex },
      // eslint-disable-next-line id-length
      (_, i) => i + 1
    );

    // Iterate over each column in the configuration
    this.config.columns.forEach((col) => {
      // Set the default value for the column if it is not already set in the editObject
      if (col.defaultValue && this.editObject[col.field] === undefined) {
        this.editObject[col.field] = col.defaultValue;
      }

      // Check if the column has a form control and if it should be included in the form
      if (
        col.formControl &&
        ((col.isCreateField && !this.editObject.id) ||
          (col.isEditField && this.editObject.id))
      ) {
        // Handle dropdown or enum types by setting the form control value to the field's value or the field itself
        if (
          (col.type === 'dropdown' || col.type === 'enum') &&
          this.editObject[col.field]
        ) {
          const formControl = new FormControl(
            this.editObject[col.field].value
              ? this.editObject[col.field].value
              : this.editObject[col.field],
            col.formControl.validators
          );

          this.form.setControl(col.field, formControl);
        } else {
          // For other types, set the form control value to the field's value
          const formControl = new FormControl(
            this.editObject[col.field],
            col.formControl.validators
          );
          this.form.setControl(col.field, formControl);
        }
      }
    });

    // Initialize the form subscription to handle form value changes
    this.initializeFormSubscription();
  }

  ngOnDestroy(): void {
    this.formChangeDetectionService.removeForm(this.form);
    this.formSubscription.unsubscribe();
  }

  /**
   * Initializes the form subscription.
   * Subscribes to the form's valueChanges observable.
   * When the form values change, it adds the form to the form array in the formChangeDetectionService
   * and emits the formChanged event with a value of true.
   *
   * @returns {void}
   */
  initializeFormSubscription(): void {
    this.formSubscription = this.form.valueChanges.subscribe((changes) => {
      if (changes) {
        this.formChangeDetectionService.addFormToFormArray(this.form);
        this.formChanged.emit(true);
      }
    });
  }

  /**
   * Checks if a given number is present in the formSeparatePageIndex attribute of any column.
   * If the editObject has an ID and the column is an edit field, it returns true.
   * If the editObject does not have an ID and the column is a create field, it returns true.
   * Otherwise, it returns false.
   *
   * @param {number} numberToCheck - The number to check against the formSeparatePageIndex attribute.
   * @returns {boolean} - True if the number is present in the formSeparatePageIndex attribute, false otherwise.
   */
  isNumberInArrayAttribute(numberToCheck: number): boolean {
    const foundObject = this.config.columns.find(
      (col) => col.formSeparatePageIndex === numberToCheck
    );
    if (foundObject) {
      if (this.editObject.id && foundObject.isEditField) {
        if (foundObject.isEditField) {
          return true;
        }
      } else if (!this.editObject.id && foundObject.isCreateField) {
        return true;
      }
    }

    return false;
  }

  /**
   * Handles the event when a file is removed.
   * Emits the formChanged event with a value of true and the fileRemovedEmitter event with the removed file.
   *
   * @param {FileDto | File} file - The file that was removed.
   * @returns {void}
   */
  fileRemoved(file: FileDto | File): void {
    if (file) {
      this.formChanged.emit(true);
      this.fileRemovedEmitter.emit(file);
    }
  }

  /**
   * Handles the event when files are chosen.
   * If the field in the edit object is an array, it adds the new files to the array.
   * Otherwise, it sets the field to the new files.
   * Updates the form control value and emits the formChanged event.
   *
   * @param {File[]} files - The array of chosen files.
   * @param {string} field - The field name in the edit object to which the files belong.
   * @returns {void}
   */
  filesChosen(files: File[], field: string): void {
    if (this.editObject[field] instanceof Array) {
      files.forEach((file) => {
        if (
          !this.editObject[field].find(
            (fileToFind: { name: string }) => fileToFind.name === file.name
          )
        ) {
          this.editObject[field].push(file);
        }
      });
      this.form.get(field)?.setValue(this.editObject[field]);
    } else {
      this.editObject[field] = files;
      this.form.get(field)?.setValue(files);
    }
    this.formChanged.emit(true);
  }

  /**
   * Checks if a form control is required.
   * Returns true if the form control has the required validator, false otherwise.
   *
   * @param {GenericColumnConfiguration<unknown>} column - The column configuration object.
   * @returns {boolean} - True if the form control is required, false otherwise.
   */
  isRequired(column: GenericColumnConfiguration<unknown>): boolean {
    const control = this.form?.get(column.field);

    return control?.hasValidator(Validators.required) ?? false;
  }
}
